import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { DetailCard } from "@/components/detail-page";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Clock,
  User,
  Edit,
  Plus,
  Trash2,
  Check,
  X,
  RefreshCw,
  FileText,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface AuditEvent {
  id: string;
  action: string;
  resourceType: string | null;
  resourceId: string | null;
  userId: string | null;
  userType: string | null;
  userRole: string | null;
  userEmail: string | null;
  userName: string | null;
  oldValues: Record<string, unknown> | null;
  newValues: Record<string, unknown> | null;
  timestamp: string;
  ipAddress: string | null;
  userAgent: string | null;
  description: string | null;
  changesSummary: string | null;
  changedFields: string[] | null;
  // Legacy fields for backwards compatibility
  actorId: string | null;
  actorType: string | null;
  customerId: string | null;
}

interface ActivityTabProps {
  entityType: string;
  entityId: string;
  createdAt: string;
  updatedAt: string;
}

const ACTION_ICONS: Record<string, typeof Clock> = {
  CREATE: Plus,
  UPDATE: Edit,
  DELETE: Trash2,
  APPROVE: Check,
  REJECT: X,
  STATUS_CHANGE: RefreshCw,
};

const ACTION_COLORS: Record<string, string> = {
  CREATE: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  UPDATE: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  DELETE: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
  APPROVE: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
  REJECT: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
  STATUS_CHANGE: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
};

function formatRelativeTime(date: string): string {
  const now = new Date();
  const past = new Date(date);
  const diffMs = now.getTime() - past.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return past.toLocaleDateString();
}

function formatAction(action: string): string {
  return action.replace(/_/g, " ").toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
}

export function ActivityTab({ entityType, entityId, createdAt, updatedAt }: ActivityTabProps) {
  const { data: auditEvents, isLoading } = useQuery<AuditEvent[]>({
    queryKey: ["audit-events", entityType, entityId],
    queryFn: async () => {
      const response = await apiRequest(
        "GET",
        `/api/audit?entityType=${entityType}&entityId=${entityId}`
      );
      if (!response.ok) {
        // If audit endpoint doesn't exist yet, return empty array
        return [];
      }
      return response.json();
    },
    refetchInterval: 5000, // Auto-refresh every 5 seconds
    refetchIntervalInBackground: true, // Continue polling even when tab is inactive
  });

  const timeline = auditEvents ?? [];

  return (
    <div className="space-y-6">
      <DetailCard title="Activity Timeline" description="Recent changes and events">
        {isLoading ? (
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="flex gap-4">
                <Skeleton className="h-10 w-10 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-48" />
                  <Skeleton className="h-3 w-32" />
                </div>
              </div>
            ))}
          </div>
        ) : timeline.length === 0 ? (
          <div className="text-center py-8">
            <Clock className="mx-auto h-12 w-12 text-muted-foreground" />
            <p className="mt-2 text-muted-foreground">No activity recorded</p>
          </div>
        ) : (
          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-5 top-0 bottom-0 w-px bg-border" />

            <div className="space-y-6">
              {timeline.map((event, index) => {
                const Icon = ACTION_ICONS[event.action] || FileText;
                const colorClass = ACTION_COLORS[event.action] || "bg-gray-100 text-gray-700";

                return (
                  <div key={event.id || index} className="relative flex gap-4 pl-2">
                    {/* Icon */}
                    <div
                      className={cn(
                        "relative z-10 flex h-10 w-10 items-center justify-center rounded-full",
                        colorClass
                      )}
                    >
                      <Icon className="h-4 w-4" />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0 pb-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium">
                            {event.description || formatAction(event.action)}
                          </p>

                          {/* Display actor info if available */}
                          {(event.userName || event.userEmail) && (
                            <div className="flex items-center gap-1.5 mt-1 text-xs text-muted-foreground">
                              <User className="h-3 w-3" />
                              <span>
                                {event.userName || event.userEmail || "System"}
                                {event.userRole && (
                                  <Badge variant="outline" className="ml-1.5 text-xs h-4 px-1">
                                    {event.userRole}
                                  </Badge>
                                )}
                              </span>
                            </div>
                          )}

                          {/* Display changes summary if available */}
                          {event.changesSummary && (
                            <div className="mt-2 text-xs text-muted-foreground">
                              <div className="font-medium mb-0.5">
                                {event.changedFields?.length || 0} field{event.changedFields && event.changedFields.length !== 1 ? 's' : ''} changed:
                              </div>
                              {(() => {
                                const changes = event.changesSummary.split(', ');
                                if (changes.length === 1) {
                                  return (
                                    <div className="text-foreground/70">
                                      {event.changesSummary}
                                    </div>
                                  );
                                }
                                return (
                                  <div className="space-y-1">
                                    {changes.map((change, idx) => (
                                      <div key={idx} className="text-foreground/70">
                                        • {change.trim()}
                                      </div>
                                    ))}
                                  </div>
                                );
                              })()}
                            </div>
                          )}

                          {/* Fallback to newValues if no summary */}
                          {!event.changesSummary && event.newValues && Object.keys(event.newValues).length > 0 && (
                            <div className="mt-1 space-y-1">
                              {Object.entries(event.newValues).slice(0, 3).map(([key, newValue]) => {
                                const oldValue = event.oldValues?.[key];
                                return (
                                  <p key={key} className="text-xs text-muted-foreground">
                                    <span className="font-medium">{formatFieldName(key)}:</span>{" "}
                                    {oldValue !== undefined && oldValue !== newValue ? (
                                      <>
                                        <span className="line-through opacity-60">{formatChangeValue(oldValue)}</span>
                                        {" → "}
                                        <span>{formatChangeValue(newValue)}</span>
                                      </>
                                    ) : (
                                      <span>{formatChangeValue(newValue)}</span>
                                    )}
                                  </p>
                                );
                              })}
                              {Object.keys(event.newValues).length > 3 && (
                                <p className="text-xs text-muted-foreground">
                                  +{Object.keys(event.newValues).length - 3} more changes
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground shrink-0">
                          <Clock className="h-3 w-3" />
                          <span title={new Date(event.timestamp).toLocaleString()}>
                            {formatRelativeTime(event.timestamp)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </DetailCard>

      {/* Metadata Card */}
      <DetailCard title="Record Timestamps">
        <div className="grid grid-cols-2 gap-4">
          <div className="p-3 rounded-lg bg-muted/50">
            <p className="text-xs text-muted-foreground">Created</p>
            <p className="text-sm font-medium">{new Date(createdAt).toLocaleString()}</p>
          </div>
          <div className="p-3 rounded-lg bg-muted/50">
            <p className="text-xs text-muted-foreground">Last Modified</p>
            <p className="text-sm font-medium">{new Date(updatedAt).toLocaleString()}</p>
          </div>
        </div>
      </DetailCard>
    </div>
  );
}

function formatFieldName(field: string): string {
  return field
    .replace(/([A-Z])/g, " $1")
    .replace(/_/g, " ")
    .replace(/^\w/, (c) => c.toUpperCase())
    .trim();
}

function formatChangeValue(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

